// src/components/ui/multi-select-filter.tsx
// Reusable multi-select dropdown filter for OPS tables.
// IR-1034 QA (Rotem): "dropdowns with multi select — above the table".
// Empty selection = no filter (show all). Designed to also serve the pending
// IR-1047 Join Links filter rework so the OPS filter convention is shared.
import { cn } from "@/lib/cn";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export interface MultiSelectOption<T extends string> {
  value: T;
  label: string;
}

/**
 * IR-1351 r5 item 3: past this many options the menu grows a search row.
 * The status/platform filters (3-8 options) stay exactly as they were — a
 * search box over five items is noise.
 */
export const SEARCHABLE_OPTION_THRESHOLD = 12;

interface MultiSelectFilterProps<T extends string> {
  label: string;
  options: MultiSelectOption<T>[];
  /** Selected values. Empty array = no filter (all). */
  selected: T[];
  onChange: (next: T[]) => void;
  className?: string;
  /** Force the search row on or off. Defaults to on past SEARCHABLE_OPTION_THRESHOLD options. */
  searchable?: boolean;
}

/**
 * The search row. Split out so the focus effect runs on each open: Radix
 * unmounts the portalled content when the menu closes.
 */
function MenuSearchRow({
  label,
  query,
  onQueryChange,
}: {
  label: string;
  query: string;
  onQueryChange: (next: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  // Radix moves focus onto the menu content itself as it opens, and its
  // `onOpenAutoFocus` escape hatch is a private prop on DropdownMenu.Content —
  // so `autoFocus` alone loses the race. Claiming focus on the next frame puts
  // the caret in the box after Radix has finished, which is what makes the
  // search row usable without a second click.
  useEffect(() => {
    const id = requestAnimationFrame(() => ref.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="p-1 pb-1.5">
      <input
        ref={ref}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        // Radix's menu typeahead swallows printable keys and jumps focus to a
        // matching item; arrows and Enter likewise belong to the menu only once
        // the operator has left the box.
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="Search…"
        aria-label={`Search ${label}`}
        className={cn(
          "w-full rounded-md border px-2 py-1 text-xs outline-none",
          "border-[var(--iris-line-2)] bg-[var(--iris-bg-2)] text-[var(--iris-fg-2)]",
          "placeholder:text-[var(--iris-fg-3)] focus-visible:border-[var(--iris-neon-cyan)]",
        )}
      />
    </div>
  );
}

export function MultiSelectFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
  className,
  searchable,
}: MultiSelectFilterProps<T>) {
  const [query, setQuery] = useState("");
  const selectedSet = new Set(selected);
  const count = selected.length;
  const showSearch = searchable ?? options.length > SEARCHABLE_OPTION_THRESHOLD;

  const needle = query.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  const toggle = (value: T) => {
    onChange(selectedSet.has(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <DropdownMenu onOpenChange={(open) => !open && setQuery("")}>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs",
          "border-[var(--iris-line-2)] bg-[var(--iris-bg-3)] text-[var(--iris-fg-2)]",
          "outline-none hover:bg-[var(--iris-bg-4)] data-[state=open]:bg-[var(--iris-bg-4)]",
          className,
        )}
      >
        <span>{label}</span>
        {count > 0 && (
          <span className="rounded-full bg-[var(--iris-fg-1)] px-1.5 text-[10px] font-semibold text-[var(--iris-bg-0)]">
            {count}
          </span>
        )}
        <ChevronDown size={12} className="text-[var(--iris-fg-4)]" />
      </DropdownMenuTrigger>
      {/*
        IR-1351 r5 item 3 (Rotem 2026-08-11): "the country selection isnt useable
        - it opens to high - use a filter with search row". The Geo picker renders
        196 countries; with no height cap the menu was taller than the window, so
        Radix flipped it above the trigger and its head ran off the top of the
        screen (her screenshot opens mid-alphabet at Thailand). Two fixes, both
        needed: the content is capped to the space Radix measured as actually
        available (never taller than the viewport, so it can never overflow off
        either edge) with the option list scrolling inside it, and a search row
        makes a long list reachable by typing instead of by dragging.
      */}
      <DropdownMenuContent
        align="start"
        collisionPadding={12}
        className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[10rem] flex-col overflow-hidden"
      >
        {showSearch && <MenuSearchRow label={label} query={query} onQueryChange={setQuery} />}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {visible.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-[var(--iris-fg-3)]">No matches</div>
          )}
          {visible.map((opt) => {
            const checked = selectedSet.has(opt.value);
            return (
              <DropdownMenuItem
                key={opt.value}
                // Keep the menu open across toggles so several can be picked.
                onSelect={(e) => {
                  e.preventDefault();
                  toggle(opt.value);
                }}
              >
                <span
                  aria-hidden
                  className={cn(
                    "flex h-3.5 w-3.5 items-center justify-center rounded border text-[10px] leading-none",
                    checked
                      ? "border-[var(--iris-neon-green)] bg-[var(--iris-neon-green)] text-[var(--iris-bg-0)]"
                      : "border-[var(--iris-line-2)]",
                  )}
                >
                  {checked ? "✓" : ""}
                </span>
                <span>{opt.label}</span>
              </DropdownMenuItem>
            );
          })}
        </div>
        {count > 0 && (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onChange([]);
            }}
            className="mt-1 justify-center text-[11px] text-[var(--iris-fg-4)]"
          >
            Clear
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
